import { FC, useEffect, useState } from 'react'
import { positions, useAlert } from 'react-alert'
import { CopyToClipboard } from 'react-copy-to-clipboard'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'

import { receiveSelector } from '@/core/services/Wallets/WalletSelector'
import { getWalletReceiveThunk } from '@/core/services/Wallets/WalletSlice'
import { useAppDispatch, useAppSelector } from '@/core/store/hooks'
import { cn } from '@bem-react/classname'
import parse from 'html-react-parser'

import { useHandleServerError } from '@/hooks/useHandleServerError'

import { Field } from '@/components/Form/Field'
import { Back } from '@/components/UI/Back'
import { Button } from '@/components/UI/Button'
import { Loading } from '@/components/UI/Loading'
import { Title } from '@/components/UI/Title'

import './styles/WalletReceive.scss'

const cnWalletReceive = cn('WalletReceive')

export const WalletReceive: FC = () => {
  const alert = useAlert()
  const { t } = useTranslation()
  const { code } = useParams<{ code: string }>()
  const dispatch = useAppDispatch()
  const { handleServerError } = useHandleServerError()

  const receive = useAppSelector(receiveSelector)

  const [isLoading, setLoading] = useState<boolean>(true)

  useEffect(() => {
    if (code) getWalletReceive(code)
  }, [code])

  useEffect(() => {
    if (receive) setValue('address', receive.address)
  }, [receive])

  const getWalletReceive = async (code: string) => {
    try {
      await dispatch(getWalletReceiveThunk(code)).unwrap()
      setLoading(false)
    } catch (error) {
      handleServerError(error)
      setLoading(false)
    }
  }

  const onClick = () => {
    if (!receive) return

    alert.show(
      `${t('receive-alert-title')}|${t('receive-alert-text', { address: receive.address })}`,
      {
        type: 'success',
        position: positions.BOTTOM_RIGHT,
      },
    )
  }

  const { register, setValue } = useForm()

  return (
    <>
      {isLoading ? (
        <Loading type='circle' minHeight='300px' />
      ) : (
        <div className={cnWalletReceive()}>
          {receive && code && (
            <>
              <div className={cnWalletReceive('top')}>
                <Back />
                <Title>
                  {t('receive-title', { code: code.toUpperCase() })}
                </Title>
              </div>

              <div className={cnWalletReceive('text')}>
                {parse(t('receive-text', { code: code.toUpperCase() }))}
              </div>

              <div className={cnWalletReceive('wrap')}>
                <div className={cnWalletReceive('qr')}>
                  <img
                    src={'data:image/png;base64,' + receive.qr}
                    alt={code.toUpperCase()}
                  />
                </div>

                <div className={cnWalletReceive('field')}>
                  <Field
                    {...register('address')}
                    label={t('receive-label')}
                    disabled
                    mb='none'
                  />
                  <CopyToClipboard text={receive.address} onCopy={onClick}>
                    <Button fluid>{t('receive-btn')}</Button>
                  </CopyToClipboard>
                </div>
              </div>
            </>
          )}
        </div>
      )}
    </>
  )
}
